// Jarvis: the always-there assistant. Press Ctrl/⌘+J (or tap the orb), type or
// talk, and it answers out loud, looks things up and takes you to the right page.
// Optional hands-free mode listens for "Hey Jarvis". Voice-out uses the agency's
// ElevenLabs voice when a key is set, otherwise the browser's own voice.
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Ear, Loader2, Mic, Send, Sparkles, Volume2, VolumeX, X } from "lucide-react";
import { api } from "@/lib/api";
import { useApi } from "@/hooks/useApi";
import { usePerm } from "@/lib/perms";
import { getModule } from "@/config/modules";
import { useActiveAccount } from "@/state/accountContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

type Msg = { role: "user" | "assistant"; content: string };
type Action = { type: "navigate"; path: string; label: string } | { type: "open_account"; accountId: number; label: string };
type Pending = { tool: string; args: Record<string, unknown>; preview: string };
type Status = { canAsk: boolean; voice: boolean; voiceId: string };

const SR: any = typeof window !== "undefined" ? (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition : null;
const store = {
  get: (k: string, d: string) => { try { return localStorage.getItem(k) ?? d; } catch { return d; } },
  set: (k: string, v: string) => { try { localStorage.setItem(k, v); } catch { /* private mode */ } },
};
const WAKE = /\b(?:hey |ok |okay )?jarvis\b[\s,.!:-]*(.*)$/i;
const YES = /^\s*(yes|yeah|yep|confirm|confirmed|send it|do it|go ahead|approve|okay send it)\b/i;
const NO = /^\s*(no|nope|cancel|stop|don't|do not|never mind)\b/i;
const QUIT = /^\W*(stop listening|that'?s all|goodbye|good bye|thanks jarvis|thank you jarvis|go to sleep)\b/i;
const IDLE_MS = 120_000;
const GREETING: Msg = { role: "assistant", content: "At your service. Ask me about your pipeline, today's calls or tickets, or tell me to add a note, tag an account, start a checklist, or draft a text." };

export function Jarvis() {
  const { can } = usePerm();
  const { data: status, reload: reloadStatus } = useApi<Status>("/api/jarvis/status");
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { activeAccountId, setActiveAccountId } = useActiveAccount();

  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState<Msg[]>(() => { try { return JSON.parse(sessionStorage.getItem("jarvis-msgs") ?? "") as Msg[]; } catch { return [GREETING]; } });
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [listening, setListening] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [voiceOn, setVoiceOn] = useState(() => store.get("jarvis-voice", "1") === "1");
  const [handsFree, setHandsFree] = useState(() => store.get("jarvis-wake", "0") === "1");
  const [pending, setPending] = useState<Pending | null>(null);
  const pendingRef = useRef<Pending | null>(null); pendingRef.current = pending;
  const [elevenKey, setElevenKey] = useState("");
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const recRef = useRef<any>(null);
    const logRef = useRef<HTMLDivElement>(null);
  const msgsRef = useRef(msgs); msgsRef.current = msgs;
  const busyRef = useRef(busy); busyRef.current = busy;

  useEffect(() => { try { sessionStorage.setItem("jarvis-msgs", JSON.stringify(msgs.slice(-30))); } catch { /* ignore */ } logRef.current?.scrollTo({ top: 1e9, behavior: "smooth" }); }, [msgs, open]);

  const stopSpeaking = useCallback(() => {
    audioRef.current?.pause(); audioRef.current = null;
    try { window.speechSynthesis?.cancel(); } catch { /* unsupported */ }
    setSpeaking(false);
  }, []);

  const speak = useCallback(async (text: string) => {
    if (!voiceOn) return;
    stopSpeaking();
    setSpeaking(true);
    const done = () => setSpeaking(false);
    const browserVoice = () => {
      if (!window.speechSynthesis) return done();
      const u = new SpeechSynthesisUtterance(text);
      u.onend = done; u.onerror = done;
      const v = window.speechSynthesis.getVoices().find((x) => /en-GB/i.test(x.lang) && /male|daniel|george/i.test(x.name)) ?? window.speechSynthesis.getVoices().find((x) => /^en/i.test(x.lang));
      if (v) u.voice = v;
      window.speechSynthesis.speak(u);
    };
    if (!status?.voice) return browserVoice();
    try {
      const res = await fetch("/api/jarvis/speak", { method: "POST", credentials: "same-origin", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ text }) });
      if (!res.ok) throw new Error(String(res.status));
      const url = URL.createObjectURL(await res.blob());
      const a = new Audio(url); audioRef.current = a;
      a.onended = () => { URL.revokeObjectURL(url); done(); };
      a.onerror = () => { URL.revokeObjectURL(url); browserVoice(); };
      await a.play();
    } catch { browserVoice(); }
  }, [voiceOn, status?.voice, stopSpeaking]);

  const run = useCallback((a: Action) => {
    if (a.type === "navigate" && a.path.startsWith("/")) navigate(a.path);
    if (a.type === "open_account") { setActiveAccountId(a.accountId); navigate(`/accounts/${a.accountId}`); }
  }, [navigate, setActiveAccountId]);

  const say = useCallback((content: string) => { setMsgs((m) => [...m, { role: "assistant", content }]); void speak(content); }, [speak]);

  const confirmPending = useCallback(async () => {
    const p = pendingRef.current;
    if (!p || busyRef.current) return;
    setBusy(true);
    try {
      const r = await api.post<{ say: string }>("/api/jarvis/confirm", { tool: p.tool, args: p.args, accountId: activeAccountId });
      setPending(null); say(r.say);
    } catch (e) { say(`That didn't go out — ${e instanceof Error ? e.message : "something went wrong"}`); }
    finally { setBusy(false); }
  }, [activeAccountId, say]);

  const send = useCallback(async (raw: string) => {
    const text = raw.trim();
    if (!text || busyRef.current) return;
    stopSpeaking();
    if (pendingRef.current && YES.test(text)) { setMsgs((m) => [...m, { role: "user", content: text }]); setInput(""); setOpen(true); void confirmPending(); return; }
    if (pendingRef.current && NO.test(text)) { setMsgs((m) => [...m, { role: "user", content: text }]); setInput(""); setPending(null); say("Cancelled. Nothing was sent."); return; }
    setPending(null);
    const next = [...msgsRef.current.filter((m) => m.content !== GREETING.content), { role: "user" as const, content: text }];
    setMsgs(next); setInput(""); setBusy(true); setOpen(true);
    try {
      const r = await api.post<{ say: string; actions: Action[]; pending?: Pending }>("/api/jarvis/ask", {
        messages: next.slice(-10),
        page: { path: pathname, title: getModule(pathname.split("/").filter(Boolean).pop() ?? "")?.title ?? "Flowio", accountId: activeAccountId },
      });
      setMsgs((m) => [...m, { role: "assistant", content: r.say }]);
      void speak(r.say);
      setPending(r.pending ?? null);
      r.actions?.forEach(run);
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Something went wrong.";
      setMsgs((m) => [...m, { role: "assistant", content: `Sorry — ${msg}` }]);
    } finally { setBusy(false); }
  }, [pathname, activeAccountId, speak, run, stopSpeaking, confirmPending, say]);

  // Ctrl/⌘+J toggles the panel.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() === "j" && (e.metaKey || e.ctrlKey) && !e.shiftKey) { e.preventDefault(); setOpen((o) => !o); }
      else if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Conversation mode: tap the mic once and Jarvis keeps listening between turns
  // (it pauses while it thinks and speaks, so it never hears itself). Say "stop
  // listening" or go quiet for two minutes to end it. Hands-free mode just waits
  // for "Hey Jarvis" and then drops into a conversation.
  useEffect(() => {
    if (!SR || !status?.canAsk) return;
    if (!(listening || handsFree) || speaking || busy) return;
    const rec = new SR();
    rec.lang = "en-US"; rec.interimResults = false; rec.continuous = true;
    let stopped = false;
    const idle = listening ? setTimeout(() => setListening(false), IDLE_MS) : null;
    rec.onresult = (ev: any) => {
      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        if (!ev.results[i].isFinal) continue;
        const heard: string = ev.results[i][0].transcript.trim();
        if (!heard) continue;
        if (listening) {
          if (QUIT.test(heard)) { setListening(false); return; }
          void send(heard); return;
        }
        const m = heard.match(WAKE);
        if (!m) continue;
        setOpen(true); setListening(true);
        if (m[1]?.trim()) void send(m[1]);
      }
    };
    rec.onerror = (e: any) => { if (e.error === "not-allowed" || e.error === "service-not-allowed") { stopped = true; setHandsFree(false); setListening(false); toast.error("Microphone access is blocked for this site."); } };
    rec.onend = () => { if (!stopped) { try { rec.start(); } catch { /* already running */ } } };
    recRef.current = rec;
    try { rec.start(); } catch { /* ignore */ }
    return () => { stopped = true; if (idle) clearTimeout(idle); rec.onend = null; try { rec.stop(); } catch { /* ignore */ } };
  }, [listening, handsFree, speaking, busy, status?.canAsk, send]);

  const saveKey = async () => {
    try {
      await api.put("/api/settings", { elevenlabs_api_key: elevenKey.trim() });
      setElevenKey(""); await reloadStatus(); toast.success("Voice connected");
    } catch (e) { toast.error(e instanceof Error ? e.message : "Couldn't save the key"); }
  };

  if (!status?.canAsk) return null;
  const active = listening || speaking || busy;

  return (
    <>
      <button type="button" onClick={() => setOpen((o) => !o)} aria-label="Talk to Jarvis (Ctrl+J)" title="Jarvis (Ctrl+J)"
        className="fixed bottom-32 right-4 z-50 flex size-12 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg transition-transform hover:scale-105 md:bottom-24">
        {active && <span className="absolute inset-0 animate-ping rounded-full bg-primary/40" />}
        {handsFree && !active && <span className="absolute -right-0.5 -top-0.5 size-3 rounded-full border-2 border-background bg-emerald-500" />}
        <Sparkles className="relative size-5" />
      </button>

      {open && (
        <div className="fixed bottom-48 right-4 z-50 flex max-h-[70vh] w-[min(24rem,calc(100vw-2rem))] flex-col overflow-hidden rounded-xl border border-border bg-background shadow-2xl md:bottom-40">
          <div className="flex items-center justify-between border-b border-border px-3 py-2">
            <div className="flex items-center gap-2 text-sm font-semibold"><Sparkles className="size-4 text-primary" />Jarvis
              {speaking && <span className="text-[11px] font-normal text-muted-foreground">speaking…</span>}
              {listening && !speaking && !busy && <span className="text-[11px] font-normal text-emerald-500">listening — just talk</span>}
            </div>
            <div className="flex items-center gap-0.5">
              {SR && (
                <Button variant="ghost" size="icon" className={cn("size-7", handsFree && "text-emerald-500")} title={handsFree ? 'Hands-free on — say "Hey Jarvis"' : 'Turn on hands-free ("Hey Jarvis")'}
                  onClick={() => { const v = !handsFree; setHandsFree(v); store.set("jarvis-wake", v ? "1" : "0"); }}><Ear className="size-4" /></Button>
              )}
              <Button variant="ghost" size="icon" className="size-7" title={voiceOn ? "Mute replies" : "Speak replies"}
                onClick={() => { const v = !voiceOn; setVoiceOn(v); store.set("jarvis-voice", v ? "1" : "0"); if (!v) stopSpeaking(); }}>
                {voiceOn ? <Volume2 className="size-4" /> : <VolumeX className="size-4" />}
              </Button>
              <Button variant="ghost" size="icon" className="size-7" onClick={() => setOpen(false)}><X className="size-4" /></Button>
            </div>
          </div>

          <div ref={logRef} className="min-h-24 flex-1 space-y-2 overflow-y-auto px-3 py-3 text-sm">
            {msgs.map((m, i) => (
              <div key={i} className={cn("max-w-[88%] rounded-lg px-3 py-2", m.role === "user" ? "ml-auto bg-primary text-primary-foreground" : "bg-muted")}>{m.content}</div>
            ))}
            {busy && <div className="flex items-center gap-2 text-xs text-muted-foreground"><Loader2 className="size-3.5 animate-spin" />Looking into it…</div>}
          </div>

          {pending && (
            <div className="space-y-2 border-t border-border bg-amber-500/10 px-3 py-2">
              <p className="text-xs font-medium">Ready to send — confirm?</p>
              <pre className="max-h-32 overflow-y-auto whitespace-pre-wrap text-xs">{pending.preview}</pre>
              <div className="flex gap-2">
                <Button size="sm" className="h-8" disabled={busy} onClick={() => void confirmPending()}>Send</Button>
                <Button size="sm" variant="outline" className="h-8" disabled={busy} onClick={() => { setPending(null); say("Cancelled. Nothing was sent."); }}>Cancel</Button>
              </div>
            </div>
          )}

          {!status.voice && can("settings.integrations.UPDATE") && (
            <div className="flex items-center gap-1.5 border-t border-border bg-muted/40 px-3 py-2">
              <Input value={elevenKey} onChange={(e) => setElevenKey(e.target.value)} type="password" placeholder="ElevenLabs API key for a real voice" className="h-8 text-xs" />
              <Button size="sm" className="h-8" disabled={!elevenKey.trim()} onClick={saveKey}>Save</Button>
            </div>
          )}

          <form className="flex items-center gap-1.5 border-t border-border p-2" onSubmit={(e) => { e.preventDefault(); void send(input); }}>
            {SR && (
              <Button type="button" variant={listening ? "default" : "outline"} size="icon" className="size-9 shrink-0" title={listening ? "Listening — tap to stop" : "Start a voice conversation"}
                onClick={() => { if (!listening) stopSpeaking(); setListening((l) => !l); }}><Mic className="size-4" /></Button>
            )}
            <Input autoFocus value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ask Jarvis…" className="h-9 text-sm" />
            <Button type="submit" size="icon" className="size-9 shrink-0" disabled={busy || !input.trim()}><Send className="size-4" /></Button>
          </form>
        </div>
      )}
    </>
  );
}
